import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useNavigationMode } from '@context/NavigationModeContext'
import { useProductReadContext } from '@context/ProductContext'

import { LogOutIcon } from '@ui/icons/LogOut.icon'

import { useWindowResize } from '@utils/domUtils'

import { isTickmillProductTypeETD } from '@model/TickmillProductType'

import { useMarketDataMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useMarketDataMenu'
import { useProfileMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useProfileMenu'
import { useRewardsHubMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useRewardsHubMenu'
import { useShowContactSupport } from '@shared/components/Navigation/menuSections/topLevelMenus/useShowContactSupport'
import { useTradeMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useTradeMenu'

import { useIBMenu } from '../../menuSections/topLevelMenus/useIBMenu'
import { useSocialTradingMenu } from '../../menuSections/topLevelMenus/useSocialTradingMenu'
import { useStatementsMenu } from '../../menuSections/topLevelMenus/useStatementsMenu'
import { useSubscriptionsMenu } from '../../menuSections/topLevelMenus/useSubscriptionsMenu'
import { useToolsMenu } from '../../menuSections/topLevelMenus/useToolsMenu'
import { useTraderMenuSections } from '../../menuSections/topLevelMenus/useTraderMenuSections'
import { useActionSections } from '../../menuSections/useActionSections'
import { MenuSection, filterMenuSections } from '../../menuSections/useMenuSections'

/**
 * orchestrator sidebar menu sections for the full layout (Setting 3)
 * on desktop, excludes items that belong in the topbar (Profile, Language, HideSensitiveData, ContactSupport, Logout).
 * sections are filtered by condition and product, same as the existing system
 */
export const useSidebarMenuSections = (): {
  sidebarSections: MenuSection[]
  actionSections: MenuSection[]
} => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const { isIBVisible } = useIBRoutesConditions()

  const traderMenuSections = useTraderMenuSections()
  const IBMenu = useIBMenu()
  const socialTradingMenu = useSocialTradingMenu()
  const subscriptionsMenu = useSubscriptionsMenu()
  const statementsMenu = useStatementsMenu()
  const toolsMenu = useToolsMenu()
  const profile = useProfileMenu()
  const { actionSections: rawActionSections } = useActionSections()
  const isMobile = useWindowResize()
  const rewardsHub = useRewardsHubMenu()
  const tradeMenu = useTradeMenu()
  const marketDataMenu = useMarketDataMenu()
  const showContactSupport = useShowContactSupport()

  const isIBRoom = useMemo(
    () => isIBVisible && !!account?.clientIntroducingBroker?.id,
    [isIBVisible, account?.clientIntroducingBroker?.id]
  )

  const isETD = useMemo(() => isTickmillProductTypeETD(product), [product])

  const { navigationMode } = useNavigationMode()

  const sidebarSections = useMemo(() => {
    const signOut: MenuSection = {
      id: 'signout',
      label: t('Profile.SignOut'),
      path: '/logout',
      icon: LogOutIcon,
      condition: isMobile,
    }

    if (isIBRoom && account?.visibilityConfiguration?.hasIBRoom) {
      if (navigationMode === 'partner') {
        return filterMenuSections([...IBMenu, signOut], product)
      }
    }

    // ETD-specific sections
    if (isETD) {
      return filterMenuSections(
        [
          ...traderMenuSections,
          subscriptionsMenu,
          statementsMenu,
          toolsMenu,
          ...(isMobile ? [profile] : []),
          ...(isMobile ? [showContactSupport] : []),
          signOut,
        ],
        product
      )
    }

    // CFD sections (SC, UK CFD, EU)
    return filterMenuSections(
      [
        tradeMenu,
        ...traderMenuSections,
        socialTradingMenu,
        rewardsHub,
        marketDataMenu,
        ...(isMobile ? [profile] : []),
        ...(isMobile ? [showContactSupport] : []),
        signOut,
      ],
      product
    )
  }, [
    t,
    tradeMenu,
    isIBRoom,
    isETD,
    account?.visibilityConfiguration?.hasIBRoom,
    product,
    traderMenuSections,
    IBMenu,
    socialTradingMenu,
    subscriptionsMenu,
    statementsMenu,
    toolsMenu,
    profile,
    isMobile,
    marketDataMenu,
    rewardsHub,
    showContactSupport,
    navigationMode,
  ])

  return {
    sidebarSections,
    actionSections: rawActionSections,
  }
}
